import type { Analytics } from 'firebase/analytics';
import type { AuthProvider, PrayerType } from '@/types';

/**
 * Analytics service for tracking user events
 * Events are sent to Firebase Analytics for reporting
 *
 * This service lazy-loads the Firebase Analytics SDK on first use
 * to reduce initial bundle size. Events are fire-and-forget style.
 */

// Lazy-loaded analytics instance and module
let analyticsInstance: Analytics | null = null;
let analyticsModule: typeof import('firebase/analytics') | null = null;
let initializationPromise: Promise<void> | null = null;

/**
 * Lazily initialize Firebase Analytics
 * Returns immediately if already initialized or not supported
 */
async function ensureAnalytics(): Promise<{
  analytics: Analytics | null;
  logEvent: typeof import('firebase/analytics').logEvent | null;
}> {
  // Already initialized
  if (analyticsInstance && analyticsModule) {
    return { analytics: analyticsInstance, logEvent: analyticsModule.logEvent };
  }

  // Initialization in progress - wait for it
  if (initializationPromise) {
    await initializationPromise;
    return { analytics: analyticsInstance, logEvent: analyticsModule?.logEvent ?? null };
  }

  // Start initialization
  initializationPromise = (async () => {
    try {
      // Dynamically import firebase/analytics
      const [firebaseAnalytics, { app }] = await Promise.all([
        import('firebase/analytics'),
        import('@/config/firebase'),
      ]);

      // Check if analytics is supported in this environment
      const isSupported = await firebaseAnalytics.isSupported();
      if (!isSupported) {
        return;
      }

      // Initialize analytics
      analyticsInstance = firebaseAnalytics.getAnalytics(app);
      analyticsModule = firebaseAnalytics;
    } catch (error) {
      console.warn('Failed to initialize Firebase Analytics:', error);
    }
  })();

  await initializationPromise;
  return { analytics: analyticsInstance, logEvent: analyticsModule?.logEvent ?? null };
}

/**
 * Helper to track events without blocking
 * Analytics failures are silently ignored (fire-and-forget)
 */
function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  // Fire-and-forget: don't await, don't block
  ensureAnalytics()
    .then(({ analytics, logEvent }) => {
      if (analytics && logEvent) {
        logEvent(analytics, eventName, params);
      }
    })
    .catch(() => {
      // Silently ignore analytics errors
    });
}

// Track page/tab views
export function trackPageView(pageName: string): void {
  trackEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href,
  });
}

// Track user check-in for Torah study
export function trackCheckIn(portion: string): void {
  trackEvent('check_in', {
    portion_name: portion,
    timestamp: new Date().toISOString(),
  });
}

// Track message sent in chat
export function trackMessageSent(): void {
  trackEvent('message_sent', {
    timestamp: new Date().toISOString(),
  });
}

// Track prayer added
export function trackPrayerAdded(type: PrayerType): void {
  trackEvent('prayer_added', {
    prayer_type: type,
    timestamp: new Date().toISOString(),
  });
}

// Track file/material added to library
export function trackFileAdded(): void {
  trackEvent('file_added', {
    timestamp: new Date().toISOString(),
  });
}

// Track user registration
export function trackRegistration(provider: AuthProvider): void {
  trackEvent('sign_up', {
    method: provider,
  });
}

// Track user sign in
export function trackSignIn(provider: AuthProvider): void {
  trackEvent('login', {
    method: provider,
  });
}

// Track tab navigation
export function trackTabChange(tabName: string): void {
  trackEvent('select_content', {
    content_type: 'tab',
    item_id: tabName,
  });
}

// Track reaction added
export function trackReactionAdded(emoji: string, messageType: 'community' | 'dm'): void {
  trackEvent('reaction_added', {
    emoji,
    message_type: messageType,
    timestamp: new Date().toISOString(),
  });
}

// Track reaction removed
export function trackReactionRemoved(emoji: string, messageType: 'community' | 'dm'): void {
  trackEvent('reaction_removed', {
    emoji,
    message_type: messageType,
    timestamp: new Date().toISOString(),
  });
}

// Track prayer hold meditation
export function trackPrayerHold(type: PrayerType, durationMs: number, prayerName: string): void {
  trackEvent('prayer_hold', {
    prayer_type: type,
    duration_ms: durationMs,
    prayer_name: prayerName,
    timestamp: new Date().toISOString(),
  });
}

// Track custom events for extensibility
export function trackCustomEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  trackEvent(eventName, params);
}
